import React, { useEffect, useState } from 'react';

import ChangeIcon from '@assets/img/icons/change.svg';
import { MultiSelectInput, SimpleLabel, SimpleTextInput } from '@components/admin/Forms';
import { Button } from '@components/Button';
import { Layout } from '@components/Layout';
import { Pagination } from '@components/Pagination';
import { QueryContainer } from '@components/QueryContainer';
import { Table, TableCellPropsUnion } from '@components/Table/Table';
import { invoiceStrings } from '@data/invoice';
import {
  availableServiceNames,
  routePlanServiceNameStrings,
  routePlanStrings,
} from '@data/routePlan';
import { useInvoiceList } from '@hooks/queries/useInvoiceList';
import { useSearch } from '@hooks/useSearch';
import { InvoiceQueryParam, invoiceQueryParam } from '@models/Invoice';
import {
  Invoice,
  orderingChargePayDayOptions,
  orderingPaymentStatusOptions,
  orderingChargeDateOptions,
} from '@models/Invoice2';
import { ServiceName } from '@models/RoutePlan';
import { useCommonStyles, makeMuiStyles, useIsMobile } from '@styles';
import { getConfig } from '@utils/config';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { formatDate, formatPrice } from '@utils/types';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { Form, Formik } from 'formik';

const strings = createStrings({
  ja: {
    title: '請求書一覧',
    dashBoard: 'ダッシュボード',
    headerTitle: '請求書',
    d2dNo: 'D2D番号',
    requestDate: '請求日',
    from: 'から',
    to: 'まで',
    search: '検索',
    creationStatus: '作成状況',
    paymentStatus: '支払状況',
    id: 'No.',
    invoiceNo: 'D2D請求番号',
    accountNumber: '台帳番号',
    claim: '請求先',
    claimAmountJPY: '請求金額(JPY)',
    claimAmountUSD: '請求金額(USD)',
    details: '詳細',
    download: '一括ダウンロード',
    shipper: 'SHIPPER',
    consignee: 'CNEE',
    serviceName: 'サービス名',
    customerNoLabel: 'お客様の管理番号',
    customerNo: (
      <>
        お客様の
        <br />
        管理番号
      </>
    ),
    shipperAndConsignee: (
      <>
        SHIPPR
        <br />
        CNEE
      </>
    ),
    deliveryDateQuantityAndReceiver: (
      <>
        納品日〜個数
        <br />
        納品先
      </>
    ),
  },
});

const useStyles = makeMuiStyles((theme, common) => {
  return {
    link: {
      color: theme.palette.primary.main,
      whiteSpace: 'pre',
    },
    container: {
      [theme.breakpoints.up('lg')]: {
        padding: '1rem 2rem',
      },
      backgroundColor: theme.palette.background.paper,
      flex: 1,
    },
    inner: {
      ...common.flexColumn,
      maxWidth: '1600px',
      padding: '1rem 1rem',
      margin: '0 auto',
    },
    searchContainer: {
      ...common.normalText,
      background: theme.palette.grey[100],
      padding: 32,
    },
    formContainer: {
      ...common.flexColumn,
      ...common.verticalSpace2,
    },
    searchButton: {
      alignSelf: 'flex-start',
      [theme.breakpoints.down('sm')]: {
        alignSelf: 'auto',
      },
    },
    priceContainer: {
      display: 'grid',
      gridTemplateColumns: '1fr 0px',
      alignItems: 'center',
      zIndex: 1,
    },
    priceGrid: {
      display: 'grid',
      gridTemplateColumns: '100px 1fr 10px 1fr',
      columnGap: '0.5rem',
    },
    dateGrid: {
      display: 'grid',
      gridTemplateColumns: 'auto 1fr 30px 1fr 30px',
      columnGap: '0.5rem',
      [theme.breakpoints.down('sm')]: {
        rowGap: '0.5rem',
        gridTemplateColumns: '100px 1fr 30px',
      },
    },
    paginationContainer: {
      ...common.flexColumn,
      flex: 1,
      justifyContent: 'flex-end',
      alignItems: 'flex-end',
    },
    startContainer: {
      display: 'grid',
      gridTemplateColumns: '4fr 4fr 4fr 7fr',
      rowGap: '1rem',
      [theme.breakpoints.down('sm')]: {
        ...common.flexColumn,
        rowGap: '0.5rem',
      },
    },
    buttonContainer: {
      ...common.flexInline,
      columnGap: '1rem',
      [theme.breakpoints.down('sm')]: {
        ...common.flexColumn,
        rowGap: '0.5rem',
      },
    },
    payDate: {
      ...common.exSmallerText,
      color: theme.palette.grey[500],
    },
    download: {
      ...common.flexInline,
      alignItems: 'flex-end',
      justifyContent: 'flex-end',
      margin: '20px 0',
    },
    textTrim: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  };
});

const firstColumnLabelWidth = 120;

export const InvoiceList: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const isMobile = useIsMobile();
  const { search, updateSearch, clearSearch } = useSearch(invoiceQueryParam);
  const invoiceList = useInvoiceList({
    d2d_no: search.d2d_no ? `Y-${search.d2d_no}` : undefined,
    d2d_charge_no: search.d2d_charge_no ? `Y-${search.d2d_charge_no}` : undefined,
    ...search,
    total_price_currency:
      search.total_price_min || search.total_price_max
        ? search.total_price_currency ?? 'JPY'
        : undefined,
    page: (search.page ?? 0) + 1,
  });
  const [checklist, setCheckList] = useState<boolean[]>([]);

  const initialValues: InvoiceQueryParam = search;

  const checkAll = (checked: boolean) => {
    const list = checklist.map(() => checked) || [];
    setCheckList(list);
  };

  const headColumns: TableCellPropsUnion[] = [
    {
      label: 'checkbox',
      type: 'checkable',
      checked: checklist.length > 0 && checklist.every((item) => item),
      onChange: (value: boolean) => {
        checkAll(value);
      },
    },
    {
      label: strings.id,
    },
    {
      label: strings.customerNo,
    },
    {
      label: routePlanStrings.serviceName,
    },
    {
      label: '',
    },
    {
      label: strings.shipperAndConsignee,
    },
    {
      label: strings.deliveryDateQuantityAndReceiver,
    },
    {
      label: invoiceStrings.total_price,
    },
    {
      label: strings.invoiceNo,
    },
    {
      label: strings.d2dNo,
    },
    {
      label: invoiceStrings.job_code,
    },
    {
      label: strings.requestDate,
      type: 'selectable',
      options: orderingChargeDateOptions,
      onChange: (value: any) => {
        updateSearch({
          page: 0,
          ordering: value,
        });
      },
    },
    {
      label: invoiceStrings.charge_pay_day,
      type: 'selectable',
      options: orderingChargePayDayOptions,
      onChange: (value: any) => {
        updateSearch({
          page: 0,
          ordering: value,
        });
      },
    },

    {
      label: strings.paymentStatus,
      type: 'selectable',
      options: orderingPaymentStatusOptions,
      onChange: (value: any) => {
        updateSearch({
          page: 0,
          payment_status: value,
        });
      },
    },
    {
      label: '',
    },
  ];

  function mapItemToTableRowData(index: number, item: Invoice): TableCellPropsUnion[] {
    const displayTotalPrice = formatPrice(
      Number(item.total_price) ?? 0,
      item.language === 'ENGLISH' ? 'USD' : 'JPY',
    );

    const isOverdue =
      item.payment_status !== '済' &&
      item.charge_pay_day &&
      dayjs(item.charge_pay_day).isBefore(dayjs());

    const isPaid = item.payment_status === '済';

    const renderItem = (children: React.ReactNode) => (
      <div
        className={clsx(
          classes.whitespacePre,
          isOverdue && classes.textErrorImportant,
          isPaid && classes.textGray700,
        )}
      >
        {children}
      </div>
    );

    return [
      {
        label: 'checkbox',
        type: 'checkable',
        checked: checklist?.[index] || false,
        onChange: (value: boolean) => {
          const list =
            checklist.map((v, i) => {
              return i === index ? value : v;
            }) || [];
          setCheckList(list);
        },
      },
      {
        label: renderItem(String(index + 1 + (search.page ?? 0) * 10)),
      },
      {
        label: renderItem(<div className={classes.textTrim}>{item.customer_no || '-'}</div>),
      },
      {
        label: renderItem(
          availableServiceNames.includes(item.service_name as ServiceName)
            ? routePlanServiceNameStrings[item.service_name as ServiceName]
            : item.service_name || '-',
        ),
      },
      {
        label: (
          <a
            href={`${getConfig().serverUrl}/invoice/v3/${item.visit_id}/download/`}
            target="_blank"
            className={clsx(
              classes.link,
              classes.underline,
              classes.whitespacePre,
              isOverdue && classes.textErrorImportant,
            )}
          >
            {invoiceStrings.invoice}
          </a>
        ),
      },
      {
        label: renderItem(
          <div className={classes.textTrim} style={{ width: '6rem' }}>
            {item.shipper_name || '-'}
            <br />
            {item.consignee_name || '-'}
          </div>,
        ),
      },
      {
        label: renderItem(
          <>
            {formatDate(item.preferred_arrival_date) || '-'}~{item.quantity || '-'}
            <div className={classes.textTrim} style={{ width: '6rem' }}>
              {item.receiver_company_name || '-'}
            </div>
          </>,
        ),
      },
      {
        label: renderItem(displayTotalPrice),
      },
      {
        label: renderItem(item.d2d_charge_no || '-'),
      },
      {
        label: (
          <a
            target="_blank"
            href={`${url('/utilities/Bookings/CurrentBookingDetails')}?d2dNo=${
              item.house_bl_number
            }`}
            className={clsx(
              classes.link,
              classes.underline,
              classes.whitespacePre,
              isOverdue && classes.textErrorImportant,
            )}
          >
            {item.house_bl_number || '-'}
          </a>
        ),
      },
      {
        label: renderItem(item.job_code || '-'),
      },
      {
        label: renderItem(formatDate(item.charge_date) || '-'),
      },
      {
        label: renderItem(formatDate(item.charge_pay_day) ?? '-'),
      },
      {
        label:
          item.payment_status === '未'
            ? renderItem(<div>{item.payment_status}</div>)
            : renderItem(
                <>
                  <div>{item.payment_status}</div>
                  <div className={classes.payDate}>
                    {invoiceStrings.finish_date} {formatDate(item.finish_date) || '-'}
                  </div>
                </>,
              ),
      },
    ];
  }

  const bodyRows: TableCellPropsUnion[][] | undefined = invoiceList.data?.results.map(
    (result, index) => {
      return mapItemToTableRowData(index, result);
    },
  );

  useEffect(() => {
    const list = invoiceList.data?.results.map(() => false) || [];
    setCheckList(() => list);
  }, [invoiceList.data?.results]);

  const invoicePageCount = 10;

  const renderPagination = () => {
    const pageCount = invoiceList.isSuccess
      ? Math.floor((invoiceList.data!.count + invoicePageCount - 1) / invoicePageCount)
      : 10;

    return (
      <Pagination
        page={Math.min(Math.max(search.page ?? 0, 0), pageCount - 1)}
        count={pageCount}
        onChange={(page) => updateSearch({ page })}
      />
    );
  };

  return (
    <Layout
      loading={invoiceList.isLoading}
      title={strings.title}
      errors={[invoiceList.error]}
      breadcrumbs={['/utilities/Dashboard', '/utilities/Invoice/InvoiceList']}
    >
      <div className={classes.container}>
        <div className={classes.inner}>
          <div
            className={clsx(classes.pageTitleText, classes.textPrimary)}
            style={{ marginBottom: '1rem' }}
          >
            {strings.title}
          </div>

          <div className={classes.searchContainer}>
            <Formik
              initialValues={initialValues}
              enableReinitialize
              onSubmit={async (values) => {
                updateSearch({
                  ...values,
                  page: 0,
                });
              }}
            >
              {({ values, setFieldValue }) => (
                <Form className={classes.formContainer}>
                  <div className={classes.startContainer}>
                    <SimpleTextInput
                      name="customer_no"
                      textInputFullWidth
                      label={strings.customerNoLabel}
                      labelProps={{ width: firstColumnLabelWidth }}
                    />

                    <SimpleTextInput
                      name="d2d_no"
                      prefix="Y-"
                      textInputFullWidth
                      label={strings.d2dNo}
                      labelProps={{ width: 100 }}
                    />

                    <SimpleTextInput
                      name="shipper"
                      textInputFullWidth
                      label={strings.shipper}
                      labelProps={{ width: 100 }}
                    />

                    <div className={classes.dateGrid}>
                      <SimpleLabel width={80}>{strings.requestDate}</SimpleLabel>

                      <SimpleTextInput name="charge_date_after" textInputFullWidth type="date" />

                      <SimpleLabel width="initial" textAlign={isMobile ? 'center' : 'right'}>
                        {strings.from}
                      </SimpleLabel>

                      {isMobile && <br />}
                      <SimpleTextInput textInputFullWidth name="charge_date_before" type="date" />

                      <SimpleLabel width="initial" textAlign={isMobile ? 'center' : 'right'}>
                        {strings.to}
                      </SimpleLabel>
                    </div>

                    <MultiSelectInput
                      name="service_name"
                      textInputFullWidth
                      label={strings.serviceName}
                      options={availableServiceNames.map((serviceName) => ({
                        label: routePlanServiceNameStrings[serviceName],
                        value: serviceName,
                      }))}
                      labelProps={{ width: firstColumnLabelWidth }}
                    />
                    <SimpleTextInput
                      name="job_code"
                      textInputFullWidth
                      label={invoiceStrings.job_code}
                      labelProps={{ width: 100 }}
                    />
                    <SimpleTextInput
                      name="cosignee"
                      textInputFullWidth
                      label={strings.consignee}
                      labelProps={{ width: 100 }}
                    />

                    <div className={classes.dateGrid}>
                      <SimpleLabel width={80}>{invoiceStrings.charge_pay_day}</SimpleLabel>

                      <SimpleTextInput name="charge_pay_day_after" textInputFullWidth type="date" />

                      <SimpleLabel width="initial" textAlign={isMobile ? 'center' : 'right'}>
                        {strings.from}
                      </SimpleLabel>

                      {isMobile && <br />}

                      <SimpleTextInput
                        textInputFullWidth
                        name="charge_pay_day_before"
                        type="date"
                      />

                      <SimpleLabel width="initial" textAlign={isMobile ? 'center' : 'right'}>
                        {strings.to}
                      </SimpleLabel>
                    </div>

                    <SimpleTextInput
                      name="quantity"
                      label={invoiceStrings.quantity}
                      textInputFullWidth
                      labelProps={{ width: firstColumnLabelWidth }}
                      type="number"
                    />

                    <SimpleTextInput
                      name="d2d_charge_no"
                      prefix="Y-"
                      textInputFullWidth
                      label={invoiceStrings.d2d_charge_no}
                      labelProps={{ width: 100 }}
                    />

                    <div className={classes.priceContainer}>
                      <div className={classes.priceGrid}>
                        <SimpleLabel width={100}>{invoiceStrings.total_price}</SimpleLabel>
                        <SimpleTextInput
                          name="total_price_min"
                          prefix={values.total_price_currency === 'USD' ? '$' : '￥'}
                          textInputFullWidth
                          type="number"
                        />
                        <SimpleLabel width={10} textAlign={'center'}>
                          ~
                        </SimpleLabel>
                        <SimpleTextInput
                          name="total_price_max"
                          prefix={values.total_price_currency === 'USD' ? '$' : '￥'}
                          textInputFullWidth
                          labelProps={{ width: 100 }}
                          type="number"
                        />
                      </div>
                      <img
                        src={ChangeIcon}
                        style={{ width: 20, marginLeft: '0.2rem' }}
                        className={classes.baseButton}
                        onClick={() =>
                          setFieldValue(
                            'total_price_currency',
                            values.total_price_currency === 'USD' ? 'JPY' : 'USD',
                          )
                        }
                      />
                    </div>

                    <div className={classes.dateGrid}>
                      <SimpleLabel width={80}>{invoiceStrings.preferred_arrival_date}</SimpleLabel>

                      <SimpleTextInput
                        textInputFullWidth
                        name="preferred_arrival_date_after"
                        type="date"
                      />

                      <SimpleLabel width="initial" textAlign={isMobile ? 'center' : 'right'}>
                        {strings.from}
                      </SimpleLabel>

                      {isMobile && <br />}

                      <SimpleTextInput
                        textInputFullWidth
                        name="preferred_arrival_date_before"
                        type="date"
                      />

                      <SimpleLabel width="initial" textAlign={isMobile ? 'center' : 'right'}>
                        {strings.to}
                      </SimpleLabel>
                    </div>
                  </div>

                  <div className={classes.buttonContainer}>
                    <Button
                      text={formStrings.filter}
                      color="primary"
                      type="submit"
                      size="small"
                      className={classes.searchButton}
                    />

                    <Button
                      text={formStrings.reset}
                      color="primary"
                      size="small"
                      type="button"
                      className={classes.searchButton}
                      onClick={clearSearch}
                    />
                  </div>
                </Form>
              )}
            </Formik>
          </div>
          <div className={classes.download}>
            <Button
              text={strings.download}
              color="primary"
              type="button"
              size="small"
              className={classes.searchButton}
              disabled={checklist.every((v) => v !== true)}
              onClick={() => {
                let exportIdsString = '';
                invoiceList.data?.results.map((item, index) => {
                  if (checklist[index]) exportIdsString += `&selected=${item.visit_id}`;
                });
                let urlString: string = `${
                  getConfig().serverUrl
                }/invoice/v3/download-zip/?${exportIdsString.substring(1, exportIdsString.length)}`;
                window.open(urlString);
              }}
            />
          </div>
          <QueryContainer queries={[invoiceList]}>
            <Table columnFixedUntil={5} fixedWidths={[50, 40, 120, 120, 80]}>
              <Table.Head>
                <Table.Row columns={headColumns} />
              </Table.Head>
              <Table.Body>
                {bodyRows?.map((columns, index) => {
                  return <Table.Row key={index} columns={columns} />;
                })}
              </Table.Body>
            </Table>
            <br />
            <div className={classes.paginationContainer}>{renderPagination()}</div>
          </QueryContainer>
        </div>
      </div>
    </Layout>
  );
};
